import React, { useState } from 'react';

interface MultiLineDataPoint {
  label: string;
  values: { [seriesName: string]: number };
}

interface MultiLineChartProps {
  data: MultiLineDataPoint[];
  series: { name: string; color: string }[];
  height?: number;
  showLegend?: boolean;
  showGrid?: boolean;
  showDots?: boolean;
  className?: string;
}

/**
 * Multi-Line Chart Component
 * Compare multiple data series over time
 */
const MultiLineChart: React.FC<MultiLineChartProps> = ({
  data,
  series,
  height = 300,
  showLegend = true,
  showGrid = true,
  showDots = true,
  className = '',
}) => {
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<{ seriesName: string; index: number } | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-gray-400 text-sm">No data available</div>
      </div>
    );
  }

  // Calculate min and max values across all series
  const allValues: number[] = [];
  data.forEach((point) => {
    series.forEach((s) => {
      if (point.values[s.name] !== undefined) {
        allValues.push(point.values[s.name]);
      }
    });
  });

  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues, 0);
  const range = maxValue - minValue || 1;
  const chartHeight = height - 80;

  const getY = (value: number) => {
    return height - 60 - ((value - minValue) / range) * chartHeight;
  };

  const getX = (index: number) => {
    return 50 + (index / (data.length - 1 || 1)) * (100 - 100 / data.length);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Chart */}
      <svg width="100%" height={height} className="overflow-visible">
        {/* Grid lines */}
        {showGrid && [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - 60 - ratio * chartHeight;
          const value = (minValue + range * ratio).toFixed(0);
          return (
            <g key={ratio}>
              <line
                x1="40"
                y1={y}
                x2="100%"
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x="5"
                y={y}
                textAnchor="start"
                dominantBaseline="middle"
                className="text-xs fill-gray-600"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Draw lines for each series */}
        {series.map((s) => {
          const points = data
            .map((point, index) => {
              const value = point.values[s.name];
              if (value === undefined) return null;
              return `${getX(index)}%,${getY(value)}`;
            })
            .filter(Boolean)
            .join(' ');

          const isHighlighted = hoveredSeries === s.name || hoveredSeries === null;

          return (
            <g key={s.name}>
              {/* Line */}
              <polyline
                points={points}
                fill="none"
                stroke={s.color}
                strokeWidth={isHighlighted ? 3 : 2}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={isHighlighted ? 1 : 0.3}
                className="transition-all duration-200"
                style={{
                  filter: isHighlighted ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' : 'none',
                }}
              />

              {/* Dots */}
              {showDots &&
                data.map((point, index) => {
                  const value = point.values[s.name];
                  if (value === undefined) return null;

                  const isPointHovered =
                    hoveredPoint?.seriesName === s.name && hoveredPoint?.index === index;

                  return (
                    <circle
                      key={index}
                      cx={`${getX(index)}%`}
                      cy={getY(value)}
                      r={isPointHovered ? 6 : isHighlighted ? 4 : 3}
                      fill="white"
                      stroke={s.color}
                      strokeWidth={isPointHovered ? 3 : 2}
                      opacity={isHighlighted ? 1 : 0.5}
                      className="transition-all duration-200 cursor-pointer"
                      onMouseEnter={() => {
                        setHoveredSeries(s.name);
                        setHoveredPoint({ seriesName: s.name, index });
                      }}
                      onMouseLeave={() => {
                        setHoveredSeries(null);
                        setHoveredPoint(null);
                      }}
                    />
                  );
                })}
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.map((point, index) => (
          <text
            key={index}
            x={`${getX(index)}%`}
            y={height - 20}
            textAnchor="middle"
            className="text-xs fill-gray-600"
          >
            {point.label.length > 8
              ? `${point.label.substring(0, 8)}...`
              : point.label}
          </text>
        ))}

        {/* Tooltip for hovered point */}
        {hoveredPoint && (
          <g>
            {(() => {
              const point = data[hoveredPoint.index];
              const value = point.values[hoveredPoint.seriesName];
              const x = getX(hoveredPoint.index);
              const y = getY(value);

              return (
                <>
                  <rect
                    x={`calc(${x}% - 60px)`}
                    y={y - 50}
                    width="120"
                    height="40"
                    fill="rgba(0, 0, 0, 0.9)"
                    rx="4"
                    className="pointer-events-none"
                  />
                  <text
                    x={`${x}%`}
                    y={y - 35}
                    textAnchor="middle"
                    className="text-xs font-semibold fill-white pointer-events-none"
                  >
                    {hoveredPoint.seriesName}
                  </text>
                  <text
                    x={`${x}%`}
                    y={y - 20}
                    textAnchor="middle"
                    className="text-sm font-bold fill-white pointer-events-none"
                  >
                    {value.toLocaleString()}
                  </text>
                </>
              );
            })()}
          </g>
        )}
      </svg>

      {/* Legend */}
      {showLegend && (
        <div className="mt-4 flex flex-wrap gap-4 justify-center">
          {series.map((s) => {
            const isHighlighted = hoveredSeries === s.name || hoveredSeries === null;
            return (
              <div
                key={s.name}
                className="flex items-center gap-2 cursor-pointer transition-opacity"
                onMouseEnter={() => setHoveredSeries(s.name)}
                onMouseLeave={() => setHoveredSeries(null)}
                style={{ opacity: isHighlighted ? 1 : 0.5 }}
              >
                <div className="flex items-center gap-1">
                  <div
                    className="w-8 h-0.5 rounded-full"
                    style={{ backgroundColor: s.color }}
                  />
                  <div
                    className="w-2 h-2 rounded-full border-2"
                    style={{
                      backgroundColor: 'white',
                      borderColor: s.color,
                    }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700">{s.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiLineChart;
